import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import SignInModal from './SignInModal';
import './MainPage.css';
import { motion } from "framer-motion";
import MagicRings from './MagicRings';
import { HiArrowTrendingUp, HiLockClosed, HiDevicePhoneMobile, HiBolt, HiArrowRight } from 'react-icons/hi2';
import ScrollStack, { ScrollStackItem } from './ScrollStack';


const MainPage = ({ onSuccessfulLogin }) => {
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSmallViewport, setIsSmallViewport] = useState(false);
  // Theme switching removed; defaults are provided via CSS variables in index.css

  // Ensure viewport meta tag exists for proper mobile scaling
  useEffect(() => {
    const existingMeta = document.querySelector('meta[name="viewport"]');
    if (existingMeta) {
      existingMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const meta = document.createElement('meta');
      meta.name = "viewport";
      meta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    const update = () => {
      setIsSmallViewport(window.innerWidth <= 360);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const openSignInModal = () => {
    setAuthMode('login');
    setIsSignInModalOpen(true);
  };

  const openSignUpModal = () => {
    setAuthMode('signup');
    setIsSignInModalOpen(true);
  };

  const closeSignInModal = () => {
    setIsSignInModalOpen(false);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setEmail('');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="main-page">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
                <img src="/images/Final Font Stocks4U1.png" alt="Stocks4U Logo" className="logo-text" style={{ width: '150px', height: 'auto' }} />
          </div>
          
          <nav className={`nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
            <a href="#home" onClick={() => setIsMobileMenuOpen(false)}>Home</a>
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
            <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)}>Pricing</a>
            <a href="#about" onClick={() => setIsMobileMenuOpen(false)}>About</a>
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
          </nav>

          <button
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          <div className="header-buttons">
            <button className="login-btn" onClick={openSignInModal}>Login</button>
            <button className="get-started-btn" onClick={openSignInModal}>Get Started</button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home" style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          <MagicRings
            color="#fc42ff"
            colorTwo="#42fcff"
            speed={1}
            ringCount={6}
            attenuation={10}
            lineThickness={2}
            baseRadius={0.35}
            radiusStep={0.1}
            scaleRate={0.1}
            opacity={1}
            blur={0}
            noiseAmount={0.1}
            rotation={0}
            ringGap={1.5}
            fadeIn={0.7}
            fadeOut={0.5}
            followMouse={true}
            mouseInfluence={0.2}
            hoverScale={1.2}
            parallax={0.05}
            clickBurst={true}
          />
        </div>
        <div className="hero-content" style={{ position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
          <h1 className="hero-title" style={{ pointerEvents: 'auto' }}>Watch. Analyse. Trade.</h1>
          <p className="hero-subtitle" style={{ pointerEvents: 'auto' }}>Trade Smarter with AI-driven insights and real time market intelligence.</p>
        </div>
      </section>

      {/* Introducing Section */}
      <section className="introducing-premium-wrap" id="introducing">
        <div className="container">
          <div className="introducing-content-premium">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="introducing-header-premium"
            >
              <h2 className="section-title-premium-new">
                Introducing the Future of <span className="gradient-text-hero">Intelligent Trading</span>
              </h2>
              <h3 className="section-subtitle-premium-new">
                <span className="gradient-text-hero-alt">AI-Powered</span> Precision. Real-Time Execution. Smarter Decisions.
              </h3>
              <p className="introducing-description-premium-new">
                Trading is no longer driven by guesswork — it’s powered by data, speed, and intelligent insights.
                Stocks4u combines advanced AI analytics with proven trading strategies to help you make faster, smarter, and more confident decisions in dynamic markets.
              </p>
            </motion.div>
            
            <div className="features-grid-premium-new">
              {[
                { 
                  id: '[1]', 
                  title: 'Alpha Intelligence', 
                  desc: 'Proprietary AI filters for high-probability signals.',
                  status: 'Scanning NSE/BSE...',
                  pills: ['Real-Time Volatility', 'Pattern Recognition'],
                  theme: 'purple',
                  icon: <HiArrowTrendingUp />,
                  delay: 0.1
                },
                { 
                  id: '[2]', 
                  title: 'Risk Guard', 
                  desc: 'Automated protection layers for every execution.',
                  status: 'Analyzing Risk...',
                  pills: ['Dynamic Stop-Loss', 'Liquidity Monitoring'],
                  theme: 'cyan',
                  icon: <HiLockClosed />,
                  delay: 0.2
                },
                { 
                  id: '[3]', 
                  title: 'Edge Terminal', 
                  desc: 'Institutional grade tools in your pocket.',
                  status: 'Live Sync...',
                  pills: ['Instant Execution', 'Smart Notifications'],
                  theme: 'purple',
                  icon: <HiDevicePhoneMobile />,
                  delay: 0.3
                },
                { 
                  id: '[4]', 
                  title: 'Quantum Speed', 
                  desc: 'Execute trades at the speed of institutional nodes.',
                  status: 'Calculating Entry...',
                  pills: ['Zero-Lag Pipeline', 'Sub-ms Processing'],
                  theme: 'cyan',
                  icon: <HiBolt />,
                  delay: 0.4
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: item.delay }}
                  className={`feature-card-new ${item.theme}-theme`}
                >
                  <div className="card-inner">
                    <div className="card-top-header">
                       <div className="card-icon-box-new">
                        {item.icon}
                      </div>
                      <div className="card-decorative-element">
                         <div className="decorative-line"></div>
                         <div className="decorative-dot"></div>
                      </div>
                    </div>
                    
                    <div className="card-status-box">
                       <span className="status-dot"></span>
                       <span className="status-text">{item.status}</span>
                    </div>

                    <h4 className="card-title-new">{item.title}</h4>
                    <p className="card-desc-new">{item.desc}</p>

                    <div className="card-pills-list">
                      {item.pills.map((pill, pIndex) => (
                        <div key={pIndex} className="capability-pill">
                           <span className="pill-dot"></span>
                           {pill}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="introducing-cta-new"
            >
              <p>Start trading smarter today — powered by AI.</p>
              <button className="premium-cta-button-new" onClick={openSignUpModal}>
                Get Started <HiArrowRight className="btn-icon" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scroll Stack Section */}
      <section className="scroll-stack-section">
        <ScrollStack 
          useWindowScroll={true} 
          itemDistance={50}
          itemStackDistance={20}
          stackPosition="15%"
          blurAmount={2}
        >
          <ScrollStackItem itemClassName="purple-card">
            <h3>Advanced AI Filtering</h3>
            <p>Our proprietary algorithms scan thousands of data points per second to identify high-probability trading setups across all major sectors.</p>
          </ScrollStackItem>
          
          <ScrollStackItem itemClassName="cyan-card">
            <h3>Institutional Grade Speed</h3>
            <p>Execute your strategies with the same low-latency infrastructure used by market-making firms, ensuring minimal slippage on every trade.</p>
          </ScrollStackItem>
          
          <ScrollStackItem itemClassName="purple-card">
            <h3>Dynamic Risk Safeguards</h3>
            <p>Integrated position sizing and automated exit strategies protect your capital from sudden market volatility and black swan events.</p>
          </ScrollStackItem>
          
          <ScrollStackItem itemClassName="cyan-card">
            <h3>Sector Alpha Insights</h3>
            <p>Identify capital flow transitions before they become obvious, allowing you to rotate into strengthening sectors ahead of the curve.</p>
          </ScrollStackItem>
        </ScrollStack>
      </section>

      {/* Sector Rotation Section */}

      <section className="sector-rotation">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, x: -140 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="phone1 phone-sector"
          >
            <div className="phone-screen">
              <img src="images/sector rotation.png" className="phone-image" alt="Sector Rotation" />
            </div>
          </motion.div>
          <h2 className="hero-title">Sector Rotation</h2>
           <ul className="ai-points">
            <li>Markets don't move stock by stock — they move Sector by Sector.</li>
            <li>Our AI-powered Sector Rotation engine tracks money flow across sectors in real time, </li>
             <li> revealing which industries are gaining strength and which are losing momentum.</li>
            <li>Trade where the strength is. Exit where the weakness begins.</li>
          </ul> 
        </div>
      </section>

      {/* Real Time Sector Data */}

      {/* Index Mover Section */}
      <section className="index-mover">
        <div className="container">
          <h2 className="hero-title">Index Mover</h2>
          <ul class="ai-points">
            <li>AI-powered insights that reveal the true stocks moving the Index</li>
            <li>Our AI decodes market momentum at its source.</li>
            <li>So you trade with precision, not prediction</li>
          </ul> 

          
          <div className="mover-tabs">
            <div className="tab active">Positive</div>
            <div className="tab">Net Positions</div>
            <div className="tab">Negative</div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, x: 140 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="phone1 phone-sector"
          >
            <div className="phone-screen">
              <img src="images/index mover.png" className="phone-image" />
            </div>
          </motion.div>
        </div>
      </section>
      


      {/* Oil Monitor Section */}
      <section className="oil-monitor">
            <div className="oil-content">
              <div className="oil-visual">
              <div className="hero-phones">
            <motion.div 
              className="phone phone-1"
              initial={{ opacity: 0, x: -140 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.35 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="phone-screen">
                <img src="images/Oi monitor 1.png" className="phone-image" />

              </div>
            </motion.div>
            
           
            
            <motion.div 
              className="phone phone-3"
              initial={{ opacity: 0, x: -140 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.35 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="phone-screen">
              <img src="images/OI Monitor 3.png" className="phone-image" />

              </div>
            </motion.div>
          </div>
              </div>
              <div className="oil-info">
                <h2 className="hero-title">OI Monitor</h2>
                <p>
OI Monitor reveals where the smart money is building positions. From long buildups to short covering – stay one step ahead of market shifts.                </p>
                <ul>
                  <li>📊 Trade Like Professional Traders</li>
                  <li>💡 Powerful Trade Ideas</li>
                  <li>🧠 10+ Trading Strategies</li>
                </ul>
                <button className="get-started-btn" onClick={openSignUpModal}>Get Started</button>
              </div>
            </div>
          </section>
           <div className="section-divider"> </div>

          {/* REPLACED CARD GRID SECTION */}
          

            
          

      {/* Why Choose StockFlex Section */}
      <section className="why-choose" id="features">
        <div className="container">
          <h2>Why Choose Stocks4u?</h2>
          <p>where human intuition meets artificial intelligence – Stocks4u delivers the edge you need to stay ahead of the market.</p>
          
          <div className="choose-grid">
            <div className="choose-card">
              <div className="choose-icon">📈</div>
              <h3>Intraday Scan</h3>
              <p>Smarter scans, sharper entries – your edge in intraday trading.</p>
            </div>
            <div className="choose-card">
              <div className="choose-icon">📊</div>
              <h3>Sector Rotation</h3>
              <p>Identify rising leaders. Exit weakening sectors.</p>
            </div>
            <div className="choose-card">
              <div className="choose-icon">📈</div>
              <h3>OI Monitor</h3>
              <p>Track OI moves and spot trends before price reacts.</p>
            </div>
            <div className="choose-card">
              <div className="choose-icon">📡</div>
              <h3>Momentum rader</h3>
              <p>Spot the fastest-moving stocks in real time.</p>
            </div>
             <div className="choose-card">
              <div className="choose-icon">🔥</div>
              <h3>Heatmap</h3>
              <p>A clear map of winners and losers, instantly.</p>
            </div>
             <div className="choose-card">
              <div className="choose-icon">🎯</div>
              <h3>Swing Zone</h3>
              <p>Identify key reversal zones before they trigger.
</p>
            </div>
            <div className="choose-card">
              <div className="choose-icon">📊</div>
              <h3>Index Mover</h3>
              <p>Track the stocks driving Nifty and Bank Nifty in real time.</p>
            </div>
            <div className="choose-card">
              <div className="choose-icon">📖</div>
              <h3>Trader Dairy</h3>
              <p>Smart Diary for smart traders. Keep every record, spot your mistakes and trade smarter.
</p>
            </div>

          </div>
        </div>
      </section>

      {/* Subscriptions Section */}
      <section className="subscriptions" id="pricing">
        <div className="container">
          <h2>Subscriptions</h2>
          <p>Choose the perfect plan for your trading journey</p>
          
          <div className="subscription-grid">
            <div className="subscription-card plan-silver">
              <div className="subscription-header">
                <div className="plan-pill">Silver</div>
                <div className="subscription-price">
                  <span className="price">₹4,999</span>
                  <span className="period">/ 3 month</span>
                </div>
              </div>
              <ul className="subscription-features">
                <li className="feature">Intraday scan</li>
                <li className="feature">Sector rotation</li>
                <li className="feature">OI Monito</li>
                <li className="feature">Momentum Radar</li>
                <li className="feature">Heat map</li>
                <li className="feature">Swing Zone</li>
                <li className="feature">Index Mover</li>
                <li className="feature">FII - DII Data</li>
                <li className="feature">Traders Diary</li>
                <li className="feature">Block deals and Bulk deal</li>
                <li className="feature">Sip Calculator</li>
                <li className="feature">Sucp Calculator</li>
                <li className="feature">Advanced watchlist</li>
                <li className="feature">Tutorial videos of this features</li>
                <li className="feature">Very fast customer support</li>
              </ul>
              <button className="subscription-btn" onClick={openSignUpModal}>Get Started</button>
            </div>
            
            <div className="subscription-card featured plan-gold">
              <div className="popular-badge">Most Popular</div>
              <div className="subscription-header">
                <div className="plan-pill">Gold</div>
                <div className="subscription-price">
                  <span className="price">₹7,999</span>
                  <span className="period">/ 6 month</span>
                </div>
              </div>
              <ul className="subscription-features">
                <li className="feature">Intraday scan</li>
                <li className="feature">Sector rotation</li>
                <li className="feature">OI Monito</li>
                <li className="feature">Momentum Radar</li>
                <li className="feature">Heat map</li>
                <li className="feature">Swing Zone</li>
                <li className="feature">Index Mover</li>
                <li className="feature">FII - DII Data</li>
                <li className="feature">Traders Diary</li>
                <li className="feature">Block deals and Bulk deal</li>
                <li className="feature">Sip Calculator</li>
                <li className="feature">Sucp Calculator</li>
                <li className="feature">Advanced watchlist</li>
                <li className="feature">Tutorial videos of this features</li>
                <li className="feature">Very fast customer support</li>
              </ul>
              <button className="subscription-btn" onClick={openSignUpModal} >Buy Now</button>
            </div>
            
            <div className="subscription-card plan-diamond">
              <div className="subscription-header">
                <div className="plan-pill">Diamond</div>
                <div className="subscription-price">
                  <span className="price">₹11,999</span>
                  <span className="period">/ 1 year</span>
                </div>
              </div>
              <ul className="subscription-features">
                <li className="feature">Intraday scan</li>
                <li className="feature">Sector rotation</li>
                <li className="feature">OI Monito</li>
                <li className="feature">Momentum Radar</li>
                <li className="feature">Heat map</li>
                <li className="feature">Swing Zone</li>
                <li className="feature">Index Mover</li>
                <li className="feature">FII - DII Data</li>
                <li className="feature">Traders Diary</li>
                <li className="feature">Block deals and Bulk deal</li>
                <li className="feature">Sip Calculator</li>
                <li className="feature">Sucp Calculator</li>
                <li className="feature">Advanced watchlist</li>
                <li className="feature">Tutorial videos of this features</li>
                <li className="feature">Very fast customer support</li>
                <li className="feature">Advanced Learning Materials</li>
              </ul>
              <button className="subscription-btn" onClick={openSignUpModal}>Buy Now</button>
            </div>
          </div>
        </div>
      </section>
      <section class="testimonial-section">
  <div class="testimonial-container">
    <h2 class="testimonial-heading">💬 What Our Users Say</h2>
    <p class="testimonial-subheading">
      Young investors across India are building wealth and learning to trade with Stocks4U. Here’s what they’re saying:
    </p>

    <div class="testimonial-grid">
    
      <div class="testimonial-card">
        <p>"I always thought the stock market was only for experts. But Stocks4U made it super simple. The design is clean, and I love the market insights."</p>
        <strong>Ananya Sharma</strong><br/>
        <small>27 – Marketing Professional, Bengaluru</small>
      </div>

      <div class="testimonial-card">
        <p>"Stocks4U is perfect for serious traders. The real-time data, advanced charts, and fast execution have made it my primary trading platform."</p>
        <strong>Rohit Mehta</strong><br/>
        <small>35 – Full-Time Trader, Mumbai</small>
      </div>

      <div class="testimonial-card">
        <p>"As a beginner, I had no clue where to start. Stocks4U's tutorials and dashboard gave me the confidence to begin investing with just ₹500!"</p>
        <strong>Simran Kaur</strong><br/>
        <small>23 – Final Year Student, Delhi</small>
      </div>

      <div class="testimonial-card">
        <p>"The platform’s analytics and design impressed me right away. Stocks4U is a must-have for anyone who loves data-driven investing."</p>
        <strong>Nikhil Bansal</strong><br/>
        <small>29 – Software Developer, Pune</small>
</div>

      <div class="testimonial-card">
        <p>"I run a startup, and Stocks4U helps me invest smartly without wasting time. Alerts, watchlists, and insights – it’s all there."</p>
        <strong>Priya Reddy</strong><br/>
        <small>31 – Entrepreneur, Hyderabad</small>
      </div>

      <div class="testimonial-card">
        <p>"I started investing after college and Stocks4U made it so easy. It’s user-friendly, secure, and helps you understand every step."</p>
        <strong>Amit Singh</strong><br/>
        <small>24 – B.Com Graduate, Lucknow</small>
      </div>

      <div class="testimonial-card">
        <p>"I’ve tried many apps, but none have the balance of simplicity and features that Stocks4U offers. It’s the one I recommend to all beginners."</p>
        <strong>Megha Rao</strong><br/>
        <small>30 – Personal Finance Blogger, Chennai</small>
      </div>

      <div class="testimonial-card">
        <p>"The reporting tools, tax summaries, and clean portfolio view save me a lot of time. It’s a well-thought-out app for professionals."</p>
        <strong>Karan Joshi</strong><br/>
        <small>38 – Chartered Accountant, Jaipur</small>
      </div>

      <div class="testimonial-card">
        <p>"The user interface is brilliant. As a designer, I appreciate the detail. As an investor, I value how easy it is to monitor and act quickly."</p>
        <strong>Ishita Deshmukh</strong><br/>
        <small>26 – UX Designer, Pune</small>
      </div>

      <div class="testimonial-card">
        <p>"I was always focused on real estate. But Stocks4U introduced me to stock investing and made diversification effortless."</p>
        <strong>Aditya Nair</strong><br/>
 <small>33 – Real Estate Consultant, Kochi</small>
      </div>

      <div class="testimonial-card">
        <p>"Being a student, I wanted to invest small amounts. The fractional investing feature helped me get started with ease."</p>
        <strong>Tanvi Patel</strong><br/>
        <small>21 – College Student, Ahmedabad</small>
      </div>

      <div class="testimonial-card">
        <p>"I’m a numbers guy. Stocks4U gives me the data, trends, and alerts I need without being cluttered or complicated."</p>
        <strong>Rahul Khanna</strong><br/>
        <small>39 – Digital Marketer, Gurgaon</small>
      </div>
    </div>
  </div>
</section>


      {/* About Us Section */}
      <section className="about-us" id="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>About Us</h2>
              <p>Stocks4u India's most trusted trading platform. Empowering thousands of traders with next-gen technology</p>
              
              <div className="about-stats">
                <div className="stat-item">
                  <div className="stat-number">5K+</div>
                  <div className="stat-label">Active Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">₹2T+</div>
                  <div className="stat-label">Volume Traded</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">99.9%</div>
                  <div className="stat-label">Uptime</div>
                </div>
              </div>
              
              <div className="mission-section">
                <h3>Our Mission</h3>
                <p>We know the pain of stock selection in a volatile market. That's why we created Stocks4u—a platform that simplifies F&O trading with clean, powerful data filters.

Our mission is simple:
To empower every trader with the right tools to trade confidently and profitably.</p>
              </div>
            </div>
            
            <div className="about-newsletter">
              <div className="newsletter-card">
                <h3>Have a Question?</h3>
                <p>Get the latest market insights and trading tips delivered to your inbox</p>
                
                <form onSubmit={handleEmailSubmit} className="newsletter-form">
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="email-input"
                    />
                    <input
                      type="doubts"
                      placeholder="Enter your doubts here"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="email-input"
                    />
                  </div>
                  <button type="submit" className="join-now-btn">Submit</button>
                </form>
                
                <div className="newsletter-benefits">
                  <div className="benefit">📈 Daily market updates</div>
                  <div className="benefit">💡 Trading tips & strategies</div>
                  <div className="benefit">🎯 Exclusive research reports</div>
                  <div className="benefit">🚀 New feature announcements</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" id="contact">
        {/* Stock Ticker */}
        <div className="stock-ticker">
          <div className="ticker-wrapper">
            <div className="ticker-content">
              <div className="ticker-item">
                <span className="ticker-symbol">NIFTY 50</span>
                <span className="ticker-price">19,745.00</span>
                <span className="ticker-change positive">+152.30</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">SENSEX</span>
                <span className="ticker-price">66,230.00</span>
                <span className="ticker-change positive">+421.80</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">BANK NIFTY</span>
                <span className="ticker-price">45,120.00</span>
                <span className="ticker-change negative">-89.50</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">NIFTY IT</span>
                <span className="ticker-price">30,248.75</span>
                <span className="ticker-change positive">+724.30</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">RELIANCE</span>
                <span className="ticker-price">2,456.75</span>
                <span className="ticker-change positive">+84.50</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">TCS</span>
                <span className="ticker-price">3,847.50</span>
                <span className="ticker-change positive">+192.25</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="footer-content">
          <div className="footer-section">
            <div className="logo">
                <img src="/images/Final Font Stocks4U1.png" alt="Stocks4U Logo" className="logo-text" />
          </div>
            <p>India's most trusted trading platform. Empowering millions of traders with cutting-edge technology.</p>
            <div className="social-links">
              <a href="#" className="social-link">📘</a>
              <a href="#" className="social-link">🐦</a>
              <a href="#" className="social-link">📸</a>
              <a href="#" className="social-link">💼</a>
              <a href="#" className="social-link">🔔 </a>
              <a href="#" className="social-link">📝</a>
              <a href="#" className="social-link">👤</a>

            </div>
          </div>
          
          <div className="footer-section">
            <h4>Trading</h4>
            <a href="#stocks">Stocks</a>
            <a href="#options">Options</a>
            <a href="#futures">Futures</a>
            <a href="#commodities">Commodities</a>
            <a href="#currencies">Currencies</a>
          </div>
          
          <div className="footer-section">
            <h4>Tools & Features</h4>
            <a href="#charts">Advanced Charts</a>
            <a href="#screener">Stock Screener</a>
            <a href="#calculator">SIP Calculator</a>
            <a href="#diary">Trader Diary</a>
            <a href="#alerts">Price Alerts</a>
          </div>
          
          <div className="footer-section">
            <h4>Support</h4>
            <a href="#help">Help Center</a>
            <a href="#contact">Contact Us</a>
            <a href="#docs">API Documentation</a>
            <a href="#tutorials">Video Tutorials</a>
            <a href="#webinars">Webinars</a>
          </div>
          
          <div className="footer-section">
            <h4>Company</h4>
            <a href="#about">About Stocks4u</a>
            <a href="#careers"></a>
        
          </div>
        </div>
        
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p>&copy; 2025 Stocks4u.All Rights Reserved</p>
            <div className="footer-links">
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
              <a href="#disclaimer">Risk Disclaimer</a>
              
            </div>
            <p> Made with ❤️ in 🇮🇳.</p>
          </div>
        </div>
      </footer>

      <Modal isOpen={isSignInModalOpen} onClose={closeSignInModal}>
        <SignInModal onClose={closeSignInModal} onSuccessfulLogin={onSuccessfulLogin} initialMode={authMode} />
      </Modal>
    </div>
  );
};

export default MainPage;